import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { View, Text, TextInput, StyleSheet, TouchableOpacity, Modal, Platform, ScrollView } from "react-native";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import { useApiWithLoading } from '../../../../../hooks/useApiWithLoading';
import { postFeeLookup } from '../../../../../services/common.service';
import { useSessionStore } from '../../../../../../../state/src/useSessionStore';
import type { SessionState } from '../../../../../../../state/src/useSessionStore';
import { ProductType } from '../../../../../view-models/productType';
import DownArrow from "../../../../../../../../assets/images/DownArrowIcon.svg";
import Colors from "../../../../../constants/colors";
import GradientBorderBox from "../../../../uiComponents/GradiendBorderBox";
import BackIcon from "../../../../../../../../assets/images/BackIcon.svg";
import CloseIcon from "../../../../../../../../assets/images/CloseIcon.svg";
import RadioSelectedIcon from "../../../../../../../../assets/images/RadioSelectButton.svg";
import { inputAssistDisabledProps } from "../../../common/FloatingTextInputComponent";
import CurrencyInput from "../../../common/CurrencyInputComponent";
import FlagWithCurrency from "../../../common/FlagWithCurrencyComponent";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  onContinue?: () => void;
  cameFromEdit?: boolean;
  testID?: string | undefined;
};

function dynamicAmountTypography(raw: string | undefined | null) {
  const len = String(raw ?? "").length;
  let fs = 40;
  if (len > 9) fs = 34;
  if (len > 12) fs = 28;
  if (len > 15) fs = 24;
  if (len > 18) fs = 20;
  if (len > 21) fs = 18;
  if (len > 24) fs = 16;
  return { fontSize: fs, lineHeight: Math.round(fs * 1.22) };
}

export default function SendingAmountEntryStep({ onValidationChange, onContinue, cameFromEdit, testID }: Props) {
  const { t } = useTranslation();
  const { executeWithLoading } = useApiWithLoading();
  const sendAmount = useSessionStore((s: SessionState) => s.currentSendAmount);
  const receiveAmount = useSessionStore((s: SessionState) => s.theyReceiveAmount);
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.selectedDestinationStateAbbr);
  const [exchangeRate, setExchangeRate] = useState<number>(1);
  const [receiveCurrency, setReceiveCurrency] = useState<string>("");
  const [willCallOptions, setWillCallOptions] = useState<Array<{ currency: string; rate: number }>>([]);
  const [selectedReceiveCurrency, setSelectedReceiveCurrency] = useState<string | null>(null);
  const setSelectedReceiveCurrencyGlobal = useSessionStore((s: SessionState) => s.setSelectedReceiveCurrency);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const setCurrentSendAmount = useSessionStore((s: SessionState) => (s as any).setCurrentSendAmount);
  const setTheyReceiveAmount = useSessionStore((s: SessionState) => (s as any).setTheyReceiveAmount);
  const [feeAmount, setFeeAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [showCurrencyModal, setShowCurrencyModal] = useState<boolean>(false);
  const [isFeeLookupLoading, setIsFeeLookupLoading] = useState<boolean>(false);

  const isValid = useMemo(() => {
    const n = parseFloat(String(sendAmount || "0").replace(/,/g, ""));
    return isFinite(n) && n > 0;
  }, [sendAmount]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);
      

  const parseAmount = (text: string): string => {
    // allow only digits and one optional decimal point
    const cleaned = text.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      return `${parts[0]}.${parts.slice(1).join("")}`;
    }
    return cleaned;
  };

  const computeReceiveFromSend = (sendStr: string | undefined, rate: number) => {
    const s = parseFloat(sendStr || "0");
    if (!isFinite(s)) return "0.00";
    const r = s * (rate || 0);
    return r.toFixed(2);
  };

  const computeSendFromReceive = (recvStr: string, rate: number) => {
    const r = parseFloat(recvStr || "0");
    if (!isFinite(r) || !rate) return "0.00";
    const s = r / rate;
    return s.toFixed(2);
  };

// Fee lookup on mount and when selection changes
useEffect(() => {
  //below code is preventing to calculate the exchange rate when user is coming from review step ( edit)
    // if (cameFromEdit) {
        // If user navigated here via "Edit" from review, skip the initial fee lookup.
      //  return;
    // }
  const doLookup = async () => {
    // Always show loader, even if IDs are missing
    await executeWithLoading(async () => {
      if (!agentId || !unitProfileId || !selectedDestinationCountryCode) {
        // Just wait until dependencies are available
        return;
      }

      try {
        setIsFeeLookupLoading(true);
        const payload: any = {
          agentID: agentId,
          unitProfileID: unitProfileId,
          sendCurrency: "USD",
          destinationCountry: selectedDestinationCountryCode,
          allOptions: true,
          productType: ProductType.SEND,
          amountExcludingFee: 1,
          amount: 1,
        };

        if (
          (selectedDestinationCountryCode === "USA" || selectedDestinationCountryCode === "CAN") &&
          selectedDestinationStateAbbr
        ) {
          payload.destinationState = selectedDestinationStateAbbr;
        }

        const res = await postFeeLookup(payload);
        console.log('postfeelookup==>', res)

        let inner: any = res;
        if (typeof res?.data === "string") {
          try {
            inner = JSON.parse(res.data);
          } catch {
            inner = res;
          }
        } else if (typeof res?.data === "object") {
          inner = res.data;
        }

        console.log('inner==>', inner)
        const feeInfosArr: any[] = Array.isArray(inner?.FeeInfos) ? inner.FeeInfos : [];
        console.log('feeInfosArr==>', feeInfosArr)
        const willCallInfos = feeInfosArr.filter((f: any) => {
          const opt = (f?.DeliveryOption ?? f?.DeliveryOptId ?? "")
            .toString()
            .toUpperCase();
          const name = (f?.DeliveryOptDisplayName ?? "")
            .toString()
            .toUpperCase();
          return opt === "WILL_CALL" || name.includes("WILL CALL");
        });
        console.log('willCallInfos==>', willCallInfos)

        const optionsMap = new Map<string, number>();
        for (const info of willCallInfos.length ? willCallInfos : feeInfosArr) {
          const rateVal =
            info?.ValidExchangeRate || info?.EstimatedExchangeRate || 1;
          const rc = info?.ReceiveAmounts?.ValidCurrencyIndicator
            ? info?.ValidReceiveCurrency || ""
            : info?.EstimatedReceiveCurrency ||
              info?.ValidReceiveCurrency ||
              "";
          const currency = (rc || "").toString();
          if (currency && !optionsMap.has(currency)) {
            optionsMap.set(currency, Number(rateVal) || 1);
          }
        }
        console.log('optionsMap==>', optionsMap)
        const options = Array.from(optionsMap.entries()).map(
          ([currency, rate]) => ({ currency, rate })
        );
        setWillCallOptions(options);
        console.log('Finally setOption==>', options)

        // if (selectedSendOption) return;

        const defaultOption =
          options.find((o) => o.currency !== "USD") ||
          options[0] ||
          { currency: "", rate: 1 };

        console.log('defaultOption setOption==>', defaultOption)
        setSelectedReceiveCurrency(defaultOption.currency || null);
        setSelectedReceiveCurrencyGlobal(defaultOption.currency || null);
        setExchangeRate(defaultOption.rate || 1);
        setReceiveCurrency(defaultOption.currency || "");
        console.log('setReceiveCurrency==>', defaultOption.currency)
        setTheyReceiveAmount(
          computeReceiveFromSend(sendAmount, defaultOption.rate || 1)
        );
      } catch (e) {
        console.error("Fee lookup failed in SendAmountEntryStep", e);
      } finally { setIsFeeLookupLoading(false); }
    }, t("STRKEY_PROCESSING"));
  };

  doLookup();
}, [
  agentId,
  unitProfileId,
  selectedDestinationCountryCode,
  selectedDestinationStateAbbr,
  selectedSendOption,
  cameFromEdit,
]);

  // Show currency chooser on load when multiple options are available and none selected
  useEffect(() => {
    if (willCallOptions.length > 1 && !selectedReceiveCurrency) {
      setShowCurrencyModal(true);
    }
  }, [willCallOptions, selectedReceiveCurrency]);

  // If user has already selected a send option in previous step, honor its rate/currency
  useEffect(() => {
    if (!selectedSendOption) return;
    const rateVal = selectedSendOption?.ValidExchangeRate || selectedSendOption?.EstimatedExchangeRate || 1;
    const rc = selectedSendOption?.ReceiveAmounts?.ValidCurrencyIndicator
      ? (selectedSendOption?.ValidReceiveCurrency || '')
      : (selectedSendOption?.EstimatedReceiveCurrency || selectedSendOption?.ValidReceiveCurrency || '');
    const rateNum = Number(rateVal) || 1;
    setExchangeRate(rateNum);
    setReceiveCurrency(rc || '');
    setSelectedReceiveCurrency(rc || null);
    setSelectedReceiveCurrencyGlobal(rc || null);
    setTheyReceiveAmount(computeReceiveFromSend(sendAmount, rateNum));

    const numericSend = parseFloat(sendAmount || "0");
    if (!isFinite(numericSend) || numericSend <= 0) {
      setFeeAmount(0);
      setTotalAmount(0);
    } else {
      const fees = Number(selectedSendOption?.SendAmounts?.TotalDiscountAmount || 0) + Number(selectedSendOption?.SendAmounts?.TotalSendFees || 0);
      setFeeAmount(fees);
      const baseTotal = Number(selectedSendOption?.TotalAmount || 0);
      setTotalAmount(baseTotal + fees);
    }
  }, [selectedSendOption]);

  // Recalculate fees/total when user edits the send amount
  useEffect(() => {
    const numericSend = parseFloat(sendAmount || "0");
    if (!selectedSendOption) {
      setFeeAmount(0);
      setTotalAmount(0);
      return;
    }
    if (!isFinite(numericSend) || numericSend <= 0) {
      setFeeAmount(0);
      setTotalAmount(0);
      return;
    }
    const computedFees = Number(selectedSendOption?.SendAmounts?.TotalDiscountAmount || 0) + Number(selectedSendOption?.SendAmounts?.TotalSendFees || 0);
    setFeeAmount(computedFees);
    const baseTotal = Number(selectedSendOption?.TotalAmount || 0);
    setTotalAmount(baseTotal + computedFees);
  }, [sendAmount, selectedSendOption]);

  console.log((Number(selectedSendOption?.TotalAmount || 0) + feeAmount).toFixed(2) , 'total amount 1');
  console.log(selectedSendOption);

  /** Shrink font as the send string grows — same rules as receive (multiline + wrap). */
  const { fontSize: sendDynamicFontSize, lineHeight: sendDynamicLineHeight } = useMemo(
    () => dynamicAmountTypography(sendAmount),
    [sendAmount]
  );

  /** Shrink font as the receive string grows so long values stay visible with wrapping (multiline). */
  const { fontSize: receiveDynamicFontSize, lineHeight: receiveDynamicLineHeight } = useMemo(
    () => dynamicAmountTypography(receiveAmount),
    [receiveAmount]
  );

  return (
    <View style={styles.container} testID={testID}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <Text style={styles.title}>{t("EXCHANGE_RATE")} {exchangeRate} {receiveCurrency}</Text>
        {/* Legacy amount UI — replaced by Atlas-style calculator rows
        <Text style={styles.youSendTitle}>{t("YOU_SEND")}</Text>
        <View style={[styles.amountInput, isFeeLookupLoading ? { opacity: 0.5 } : undefined]}>
          <View style={styles.amountFieldRow}>
            <View style={styles.amountValueInputWrap}>
              <TextInput
                testID="sendAmount"
                style={[
                  styles.amountText,
                  styles.amountTextYouSend,
                  {
                    fontSize: sendDynamicFontSize,
                    lineHeight: sendDynamicLineHeight,
                  },
                ]}
                keyboardType="decimal-pad"
                placeholder="0.00"
                placeholderTextColor={Colors.subTitleColor}
                editable={!isFeeLookupLoading}
                multiline={false}
                textAlign="center"
                value={sendAmount}
                onChangeText={(t) => {
                  const next = parseAmount(t);
                  setCurrentSendAmount?.(next);
                  setTheyReceiveAmount(computeReceiveFromSend(next, exchangeRate));
                }}
                {...inputAssistDisabledProps}
              />
            </View>
            <View style={styles.amountCurrencyColumn}>
              <View style={styles.amountCurrencyInner}>
                <Flag iso3="USA" width={16} height={16} />
                <Text style={styles.currencyText}>USD</Text>
              </View>
            </View>
          </View>
        </View>
        <Text style={[styles.youSendTitle, {marginTop: 24}]}>{t("THEY_RECEIVE")}</Text>
        <View style={[styles.amountInput, { marginBottom: 48 }, isFeeLookupLoading ? { opacity: 0.5 } : undefined]}>
          <View style={styles.amountFieldRow}>
            <View style={styles.amountValueInputWrap}>
              <TextInput
                testID="receiveAmount"
                style={[
                  styles.amountText,
                  styles.amountTextReceive,
                  {
                    fontSize: receiveDynamicFontSize,
                    lineHeight: receiveDynamicLineHeight,
                  },
                ]}
                keyboardType="decimal-pad"
                placeholder="0.00"
                placeholderTextColor={Colors.subTitleColor}
                editable={!isFeeLookupLoading}
                multiline={false}
                textAlign="center"
                value={receiveAmount}
                onChangeText={(t) => {
                  const next = parseAmount(t);
                  setTheyReceiveAmount(next);
                  setCurrentSendAmount(computeSendFromReceive(next, exchangeRate));
                }}
                {...inputAssistDisabledProps}
              />
            </View>
            <TouchableOpacity
              disabled={willCallOptions.length <= 1 || isFeeLookupLoading}
              onPress={() => setShowCurrencyModal(true)}
              testID="receiveCurrencyButton"
              style={styles.amountCurrencyColumn}
            >
              <View style={styles.amountCurrencyInner}>
                <Flag iso3={selectedDestinationCountryCode ?? ""} width={16} height={16} />
                <View style={styles.receiveCurrencyLabelRow}>
                  <Text style={styles.currencyText}>{receiveCurrency || "—"}</Text>
                  {willCallOptions?.length > 1 && <DownArrow width={16} height={16} />}
                </View>
              </View>
            </TouchableOpacity>
          </View>
        </View>
        */}
        <View
          style={[
            styles.calculatorAmounts,
            isFeeLookupLoading ? { opacity: 0.5 } : undefined,
          ]}
        >
          <View style={styles.calculatorSection}>
            <Text style={styles.calculatorLabel}>{t("YOU_SEND")}</Text>
            <View style={styles.calculatorRow}>
              <CurrencyInput
                testID="sendAmount"
                value={sendAmount ?? ""}
                formatOnBlur
                onChangeText={(next) => {
                  const parsed = parseAmount(next);
                  setCurrentSendAmount?.(parsed);
                  setTheyReceiveAmount(
                    computeReceiveFromSend(parsed, exchangeRate),
                  );
                }}
                fontSize={sendDynamicFontSize}
                editable={!isFeeLookupLoading}
              />
              <FlagWithCurrency currencyCode="USD" countryIso3="USA" />
            </View>
          </View>
          <View style={[styles.calculatorSection, styles.calculatorSectionLast]}>
            <Text style={styles.calculatorLabel}>{t("THEY_RECEIVE")}</Text>
            <View style={styles.calculatorRow}>
              <CurrencyInput
                testID="receiveAmount"
                value={receiveAmount ?? ""}
                formatOnBlur
                onChangeText={(next) => {
                  const parsed = parseAmount(next);
                  setTheyReceiveAmount(parsed);
                  setCurrentSendAmount(
                    computeSendFromReceive(parsed, exchangeRate),
                  );
                }}
                fontSize={receiveDynamicFontSize}
                editable={!isFeeLookupLoading}
              />
              <FlagWithCurrency
                currencyCode={receiveCurrency || "—"}
                countryIso3={selectedDestinationCountryCode ?? ""}
                showDropdown={willCallOptions?.length > 1}
                disabled={willCallOptions.length <= 1 || isFeeLookupLoading}
                onPress={() => setShowCurrencyModal(true)}
                testID="receiveCurrencyButton"
              />
            </View>
          </View>
        </View>
        {/* <Text style={styles.title}>{t("EXCHANGE_RATE")} {exchangeRate} {receiveCurrency}</Text> */}
        <GradientBorderBox>
          <Text style={styles.subtitle}>{t("AMOUNT_ENTRY_SUBTITLE")}</Text>
        </GradientBorderBox>
        {/* Continue moved to MainSendCashScreen gradient footer (PayAmountStep pattern)
        <View style={styles.scrollSpacer} />
        <TouchableOpacity
          testID="continueButton"
          style={styles.continueButton}
          onPress={() => onContinue?.()}
        >
          <Text style={styles.continueButtonText}>{t("STRKEY_CONTINUE")}</Text>
        </TouchableOpacity>
        */}
      </ScrollView>

      {/* {!!selectedSendOption?.DeliveryOptDisplayName && (
        <Text style={{ fontSize: 13, color: '#6B7280', marginTop: -8, marginBottom: 12 }}>
          {selectedSendOption.DeliveryOptDisplayName}
        </Text>
      )} */}
      {/* Bottom-sheet currency chooser */}
      <Modal visible={showCurrencyModal} animationType="slide" transparent={true}>
        <View style={styles.modalOverlay}>
          <View style={styles.bottomSheet}>
            <View style={styles.modalHeaderRow}>
              <TouchableOpacity onPress={() => setShowCurrencyModal(false)} testID="receiveCurrencyClose">
                <BackIcon width={24} height={24}/>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setShowCurrencyModal(false)} testID="receiveCurrencyClose">
                <CloseIcon width={24} height={24}/>
              </TouchableOpacity>
            </View>
            <Text style={styles.chooseCurrency}>{t("CHOOSE_CURRENCY_YOUR_RECEIVER_GETS")}</Text>
            {willCallOptions.map((opt) => (
              <TouchableOpacity
              rejectResponderTermination
                key={opt.currency}
                style={styles.currencyModalOptionRow}
                onPress={() => {
                  setSelectedReceiveCurrency(opt.currency);
                  setSelectedReceiveCurrencyGlobal(opt.currency);
                  setReceiveCurrency(opt.currency);
                  const selectedCurrencyUpper = (selectedSendOption?.ReceiveAmounts?.ValidCurrencyIndicator
                    ? (selectedSendOption?.ValidReceiveCurrency || '')
                    : (selectedSendOption?.EstimatedReceiveCurrency || selectedSendOption?.ValidReceiveCurrency || '')).toString().toUpperCase();
                  const isSelectedCurrency = selectedSendOption && (selectedCurrencyUpper === opt.currency.toUpperCase());
                  const rateToUse = isSelectedCurrency
                    ? Number(selectedSendOption?.ValidExchangeRate || selectedSendOption?.EstimatedExchangeRate || opt.rate)
                    : opt.rate;
                  setExchangeRate(rateToUse);
                  setTheyReceiveAmount(computeReceiveFromSend(sendAmount, rateToUse));
                  setShowCurrencyModal(false);
                }}
              >
                <Text style={styles.currencyOptionText}>{opt.currency}</Text>
                <View style={[styles.radioOuter, selectedReceiveCurrency === opt.currency && styles.radioOuterSelected]}>
                  {selectedReceiveCurrency === opt.currency && 
                    <RadioSelectedIcon width={20} height={20}/>
                  }
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: 16,
    paddingTop: 32,
    backgroundColor: Colors.white,
  },
  scroll: {
    flex: 1,
    width: "100%",
    ...(Platform.OS === "web" && { minHeight: 0 as any }),
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: "center",
    width: "100%",
    paddingBottom: 130,
  },
  scrollSpacer: {
    flexGrow: 1,
    alignSelf: "stretch",
  },
  continueButton: {
    backgroundColor: Colors.black,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
    alignSelf: "stretch",
    width: "100%",
  },
  continueButtonText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "600",
    color: Colors.white,
    lineHeight: 20,
  },
  title: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 22,
    letterSpacing: 0.07,
    textAlign: "center",
    alignSelf: "stretch",
    width: "100%",
    // marginVertical: 48
  },
  youSendTitle: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.subTitleColor,
    lineHeight: 22,
    marginBottom: 6,
    marginTop: 48,
    textAlign: "center",
    alignSelf: "stretch",
    width: "100%",
  },
  calculatorAmounts: {
    gap: 24,
    alignItems: "center",
    alignSelf: "stretch",
    width: "100%",
    marginTop: 32,
  },
  calculatorSection: {
    alignItems: "center",
    gap: 8,
  },
  calculatorSectionLast: {
    marginBottom: 32,
  },
  calculatorLabel: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "600",
    color: Colors.subTitleColor,
    lineHeight: 22,
    textAlign: "center",
  },
  calculatorRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    gap: 8,
  },
  flagOverflow: {
    width: 24,
    height: 24,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
    exchangeRate: {
    fontSize: 16,
    color: '#4CAF50',
    fontWeight: '500',
  },
  amountInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 6,
    alignSelf: "stretch",
    width: "100%",
    // marginBottom: 200
  },
  amountFieldRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
    width: "100%",
  },
  amountValueInputWrap: {
    flex: 1,
    minWidth: 0,
    alignSelf: "stretch",
  },
  amountCurrencyColumn: {
    flexShrink: 0,
    paddingTop: 4,
  },
  amountCurrencyInner: {
    alignItems: "center",
    justifyContent: "center",
  },
  receiveCurrencyLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
  },
  centeredRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    position: "absolute",
    left: "50%",
  },
  amountText: {
    fontFamily: 'Rubik',
    fontSize: 40,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 46,
    textAlign: "center",
    minWidth: 60,
    maxWidth: 200,
    backgroundColor: 'transparent',
    paddingVertical: 0,
    paddingHorizontal: 0,
    textAlignVertical: "center",
    includeFontPadding: false,
    ...( { outlineWidth: 0 } as any ),
  },
  /** Overrides on `amountText` for send: no maxWidth cap; single-line (not multiline) avoids extra row height + bottom gap. */
  amountTextYouSend: {
    maxWidth: "100%" as any,
    width: "100%",
    flexShrink: 1,
    paddingTop: 0,
    paddingBottom: 0,
    ...(Platform.OS === "web"
      ? ({ outlineStyle: "none", outlineWidth: 0 } as any)
      : {}),
  },
  /** Same as you-send: single-line avoids multiline min-height + bottom gap inside the field. */
  amountTextReceive: {
    maxWidth: "100%" as any,
    width: "100%",
    flexShrink: 1,
    paddingTop: 0,
    paddingBottom: 0,
    ...(Platform.OS === "web"
      ? ({ outlineStyle: "none", outlineWidth: 0 } as any)
      : {}),
  },
  currencyText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: "500",
    color: Colors.subTitleColor,
    lineHeight: 18
  },
  amountInput1: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 15,
    // borderRadius: 12,
    // borderWidth: 1,
    // borderColor: '#e0e0e0',
  },
  amountText1: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  amountTextGreen: {
    color: '#4CAF50',
  },
  currencyText1: {
    fontSize: 16,
    color: '#666',
  },
  dropdownArrow: {
    marginLeft: 4,
    fontSize: 10,
    color: '#666'
  },
  currencyOptionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  },
  currencyOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    width: '48%'
  },
  currencyOptionText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "400",
    color: Colors.darkGrey,
    lineHeight: 24,
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: '#D1D1D1'
  },
  radioOuterSelected: { borderColor: '#000' },
  radioInner: { width: 9, height: 9, borderRadius: 4.5, backgroundColor: '#000' },
  readOnlyInput: {
    backgroundColor: '#fafafa',
  },
  subtitle: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 22,
    letterSpacing: 0.07
  },
  instantView: {
    borderWidth: 1,
    borderColor: Colors.black,
    paddingHorizontal: 26,
    paddingVertical: 12,
    borderRadius: 6,
    marginTop: 32
  },
  pinContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  pinInput: {
    width: 60,
    height: 60,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 12,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: "#F9FAFB",
    marginRight: 24
  },
  error: {
    color: "red",
    fontSize: 12,
    marginTop: 8,
  },
  card: {
    alignItems: "center",
    // width: 327,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 16,
  },
  feeLabel: {
    fontSize: 16,
    fontWeight: "500",
  },
  oldPrice: {
    fontSize: 16,
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "green",
  },
  button: {
    backgroundColor: "black",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 24,
    // marginHorizontal: 30,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  buttonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.4)'
  },
  bottomSheet: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700'
  },
  modalClose: {
    fontSize: 18,
    color: '#666'
  },
  chooseCurrency: {
    fontFamily: 'Rubik',
    fontSize: 24,
    fontWeight: "500",
    color: Colors.modalTitle,
    lineHeight: 30,
    paddingRight: 50,
    marginBottom: 24
  },
  currencyModalOptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 32,
  }
});
